import React, { useState, useEffect } from 'react';
import axiosInstance from '../api/axiosInstance';
import { useNavigate } from 'react-router-dom';
import './AdminCommon.css';

interface Category {
  id: number;
  parent_id?: number;
  name: string;
  type: string;
  description?: string;
}

const CategoryManagePage: React.FC = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState({
    parent_id: '',
    name: '',
    type: 'main',
    description: ''
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await axiosInstance.get('/api/categories');
      setCategories(res.data);
    } catch (err) {
      console.error('카테고리 로딩 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        parent_id: formData.parent_id ? parseInt(formData.parent_id) : null
      };

      if (editingCategory) {
        await axiosInstance.put(`/api/categories/${editingCategory.id}`, data);
      } else {
        await axiosInstance.post('/api/categories', data);
      }

      setShowForm(false);
      setEditingCategory(null);
      setFormData({ parent_id: '', name: '', type: 'main', description: '' });
      fetchCategories();
      alert(editingCategory ? '카테고리가 성공적으로 수정되었습니다.' : '새 카테고리가 추가되었습니다.');
    } catch (err: any) {
      alert('오류가 발생했습니다: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      parent_id: category.parent_id?.toString() || '',
      name: category.name,
      type: category.type,
      description: category.description || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('이 카테고리를 삭제하시겠습니까? 관련 하위 항목이나 연계 데이터가 함께 영향을 받을 수 있습니다.')) return;
    
    try {
      await axiosInstance.delete(`/api/categories/${id}`);
      fetchCategories();
      alert('카테고리가 성공적으로 삭제되었습니다.');
    } catch (err: any) {
      alert('삭제에 실패했습니다: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingCategory(null);
    setFormData({ parent_id: '', name: '', type: 'main', description: '' });
  };

  const getCategoryTypeBadge = (type: string) => {
    switch (type) {
      case 'main': return <span className="admin-badge admin-badge-primary">대분류</span>;
      case 'sub1': return <span className="admin-badge admin-badge-success">중분류</span>;
      case 'sub2': return <span className="admin-badge admin-badge-warning">소분류</span>;
      default: return <span className="admin-badge">{type}</span>;
    }
  };

  // 카테고리 계층 구조 표시 리뉴얼
  const renderCategoryTree = (parentId: number | null = null, level: number = 0) => {
    return categories
      .filter(cat => cat.parent_id === parentId)
      .map(category => (
        <React.Fragment key={category.id}>
          <tr>
            <td>
              <div className="category-tree-line">
                {Array.from({ length: level }).map((_, i) => (
                  <span key={i} className="category-tree-indent" />
                ))}
                <span className="category-tree-node">
                  {category.name}
                </span>
              </div>
            </td>
            <td>{getCategoryTypeBadge(category.type)}</td>
            <td style={{ color: category.description ? 'var(--admin-text-main)' : 'var(--admin-text-muted)', fontSize: '13px' }}>
              {category.description || '작성된 설명이 없습니다.'}
            </td>
            <td>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  onClick={() => handleEdit(category)}
                  className="admin-btn admin-btn-secondary admin-btn-sm"
                  style={{ padding: '6px 12px' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '2px' }}>
                    <path d="M12 20h9"></path>
                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                  </svg>
                  수정
                </button>
                <button
                  onClick={() => handleDelete(category.id)}
                  className="admin-btn admin-btn-danger admin-btn-sm"
                  style={{ padding: '6px 12px' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '2px' }}>
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                  삭제
                </button>
              </div>
            </td>
          </tr>
          {renderCategoryTree(category.id, level + 1)}
        </React.Fragment>
      ));
  };

  const getParentOptions = () => {
    return categories.filter(cat => cat.type === 'main' || cat.type === 'sub1');
  };

  if (loading) {
    return (
      <div className="admin-page-wrapper" style={{ textAlign: 'center', padding: '48px 0' }}>
        <div style={{ color: 'var(--admin-text-muted)', fontSize: '16px' }}>카테고리 시스템 분석 중...</div>
      </div>
    );
  }

  return (
    <div className="admin-page-wrapper">
      <div className="admin-page-header">
        <div>
          <h2 className="admin-page-title">분류/카테고리 구성</h2>
          <div className="admin-page-subtitle">아카이브 콘텐츠를 그룹화하고 계층(대/중/소) 구조를 빌드하는 공간입니다.</div>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="admin-btn admin-btn-success"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          새 분류 체계 정의
        </button>
      </div>

      {categories.length === 0 ? (
        <div className="admin-empty-state">
          <svg className="admin-empty-icon" xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
          </svg>
          <div style={{ fontWeight: 600, fontSize: '16px', color: 'var(--admin-text-main)', marginBottom: '4px' }}>구성된 카테고리가 없습니다.</div>
          <div style={{ fontSize: '13px' }}>우측 상단의 "새 분류 체계 정의" 버튼을 눌러 첫 대분류를 추가해 보세요.</div>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th style={{ width: '40%' }}>분류 계층 구조명</th>
                <th style={{ width: '15%' }}>유형 타입</th>
                <th>상세 명세 설명</th>
                <th style={{ width: '20%' }}>작업</th>
              </tr>
            </thead>
            <tbody>
              {renderCategoryTree()}
            </tbody>
          </table>
        </div>
      )}

      {/* 카테고리 등록/수정 모달 - 글래스모피즘 */}
      {showForm && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-content">
            <h3 className="admin-section-title" style={{ marginTop: 0, paddingBottom: '12px', borderBottom: '1px solid var(--admin-border-color)' }}>
              {editingCategory ? '분류 체계 수정' : '새 분류 체계 생성'}
            </h3>
            
            <form onSubmit={handleSubmit} style={{ marginTop: '20px' }}>
              <div className="admin-form-group">
                <label className="admin-label">상위 상속 분류군</label>
                <select
                  value={formData.parent_id}
                  onChange={e => setFormData({ ...formData, parent_id: e.target.value })}
                  className="admin-select"
                >
                  <option value="">최상위 분류 (상위 없음)</option>
                  {getParentOptions()
                    .filter(cat => !editingCategory || cat.id !== editingCategory.id) // 자신을 상위 카테고리로 지정하는 것 방지
                    .map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name} ({cat.type === 'main' ? '대분류' : '중분류'})
                      </option>
                    ))}
                </select>
              </div>

              <div className="admin-form-group">
                <label className="admin-label">카테고리 분류명 *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder="예: 근현대사, 조선왕조실록"
                  className="admin-input"
                  required
                />
              </div>

              <div className="admin-form-group">
                <label className="admin-label">분류 수준 레벨 *</label>
                <select
                  value={formData.type}
                  onChange={e => setFormData({ ...formData, type: e.target.value })}
                  className="admin-select"
                  required
                >
                  <option value="main">대분류 (Main Category)</option>
                  <option value="sub1">중분류 (Sub1 Category)</option>
                  <option value="sub2">소분류 (Sub2 Category)</option>
                </select>
              </div>

              <div className="admin-form-group">
                <label className="admin-label">상세 명세 설명</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  placeholder="이 분류체계가 어떠한 성격의 콘텐츠를 수집하는지 명세합니다."
                  className="admin-textarea"
                />
              </div>

              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '28px' }}>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="admin-btn admin-btn-secondary"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="admin-btn admin-btn-primary"
                >
                  {editingCategory ? '적용 및 수정' : '분류 추가'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryManagePage;
